import { RulesLogic } from "json-logic-js";
import { Duration } from "date-fns";
import { JsonSchema, Layout } from "@jsonforms/core";
import { ModdleElement } from "bpmn-js/lib/BaseModeler";

export interface BoundDefinition {
  code: string,
  label: string,
}

export enum NodeType {
  activity = 'activity',
  simple = 'simple',
  external = 'external',
  service = 'service',
}

export class BaseNodeDefinition {
  id: string ;
  type?: NodeType;
  service?: string;
  code!: string;
  params: any;
  start!: boolean;
  initContext?: any;
  caseContext?: any;
  caseStatus?: any;
  caseOwner?: any;
  inputs!: BoundDefinition[];
  outputs!: BoundDefinition[];
  task!: boolean;
  caseRelationSettings?: any;
  retry?: {
    maxRetries?: number,
    multiplier?: number,
    minDelay?: number, // seconds
    maxDelay?: number, // seconds
    successOutput?: string,
  }
  constructor(elements: ModdleElement) {
    this.id = elements.id;
    this.type = elements.type || NodeType.simple;
    this.service = elements.service || '';
    this.code = elements.code || '';
    this.params = elements.params || {};
    this.start = elements.start || false;
    this.initContext = elements.initContext || {};
    this.caseContext = elements.caseContext || {};
    this.caseStatus = elements.caseStatus || {};
    this.caseOwner = elements.caseOwner || {};
    this.inputs = elements.inputs || [];
    this.outputs = elements.outputs || [];
    this.task = elements.task || false;
    this.caseRelationSettings = elements.caseRelationSettings || {};
    this.retry = elements.retry || {};
  }

}

export class SimpleNodeDefinition extends BaseNodeDefinition {
  type!: NodeType.simple;
  label?: string;
  constructor(elements: ModdleElement) {
    super(elements);
    this.type = NodeType.simple;
  }
}

export class ExternalNodeDefinition extends BaseNodeDefinition {
  type!: NodeType.external;
  constructor(elements: ModdleElement) {
    super(elements);
    this.type = NodeType.external;
  }
}

type Actions = { [action: string]: RulesLogic };

export interface ActivityFormDefinition {
  code: string,
  readonly?: boolean,
  data?: any,
  restrictedDataRules?: {
    scope: string,
    condition: RulesLogic,
  }[],
}

export class ActivityNodeDefinition extends BaseNodeDefinition {
  type!: NodeType;
  label?: string;
  codeTemplate?: string;
  owner?: string;
  group?: string;
  data?: any;
  startCondition?: RulesLogic;
  resume?: boolean;
  resumeCondition?: RulesLogic; 
  form?: ActivityFormDefinition;
  icon?: string;
  dataTemplate?: string;
  dueAtDuration?: Duration;
  caseRelationSettings?: any;
  actions?: {
    user?: Actions,
    owner?: Actions,
    observer?: Actions,
    group?: Actions,
    caseOwner?: Actions,
    caseCreatedBy?: Actions,
    roles?: { [role: string]: Actions },
    groups?: { [group: string]: Actions },
  };
  actionParams?: { [action: string]: any };
  constructor(elements: ModdleElement) {
    super(elements);
    this.type = elements.type || NodeType.activity;
    this.label = elements.label || '';
    this.codeTemplate = elements.codeTemplate || '';
    this.owner = elements.owner || '';  
    this.group = elements.group || '';
    this.data = elements.data || {};
    this.startCondition = elements.startCondition || {};
    this.resume = elements.resume || false;
    this.resumeCondition = elements.resumeCondition || {};
    this.form = elements.form || {};
    this.icon = elements.icon || '';
    this.dataTemplate = elements.dataTemplate || '';
    this.dueAtDuration = elements.dueAtDuration || {};
    this.caseRelationSettings = elements.caseRelationSettings || {};
    this.actions = elements.actions || {};
    this.actionParams = elements.actionParams || {};

  }
}

export type NodeDefinition = ActivityNodeDefinition | SimpleNodeDefinition | ExternalNodeDefinition;

export class LinkDefinition {
  source!: {
    node: string,
    output: string,
  };
  target!: {
    node: string,
    input: string,
  };
  constructor(incoming: ModdleElement) {
    this.source = { node: incoming.sourceRef?.code || incoming.sourceRef?.name, output: incoming?.name || 'output' };
    this.target = { node: incoming.targetRef?.code, input: 'input' };
  }
}

export interface FormFieldDefinition {
  label: string,
  name: string,
  type: string,
  readonly?: boolean,
  value?: any,
}

export interface FormDefinition {
  code: string,
  schema: JsonSchema,
  uischema: Layout,
  scripts?: any[],
  transformers?: { root?: string, deps: string[], template: any }[],
  data?: any,
}

export interface FilesUploadSettings {
  maxFiles: number;
  allowedExtensions: string[];
  maxSize: number;
  minSize: number;
  role?: string;
}
export interface ProcessDefinition {

  definitions?: ModdleElement;
  code: string;
  label: string;
  title: string,
  icon?: string;
  codeTemplate: string;
  nodes: { [code: string]: NodeDefinition },
  links: LinkDefinition[],
  forms: { [code: string]: FormDefinition },
  form: any,
  view: any,
  filterCategory: any,
  filters: any,
  owner?: string,
  actions?: {
    user?: Actions,
    owner?: Actions,
    group?: Actions,
    createdBy?: Actions,
    roles?: { [role: string]: Actions },
    groups?: { [group: string]: Actions },
  },
  activityActions?: ActivityNodeDefinition['actions'],
  actionParams?: { [action: string]: any },
  filesUploadSettings?: FilesUploadSettings;
  caseFileCondition?: string;
}

export class MakinaProcess {
  code: string;
  label: string;
  title: string;
  icon?: string;
  codeTemplate: string;
  nodes: { [code: string]: NodeDefinition };
  links: LinkDefinition[];
  forms: { [code: string]: FormDefinition };
  form: any;
  view: any;
  filterCategory: any;
  filters: any;
  owner?: string;
  actions?: {
    user?: Actions,
    owner?: Actions,
    group?: Actions,
    createdBy?: Actions,
    roles?: { [role: string]: Actions },
    groups?: { [group: string]: Actions },
  };
  activityActions?: ActivityNodeDefinition['actions'];
  actionParams?: { [action: string]: any };
  filesUploadSettings?: FilesUploadSettings;
  caseFileCondition?: string;
  definitions!: ModdleElement;
  constructor(elements: ModdleElement) {
    this.definitions = elements;
    // TODO: handle multiple planes
    const plane = elements.rootElements?.length > 0 ? elements.rootElements[0] : null;

    this.code = plane?.code || '';
    this.label = plane?.label || '';
    this.title = plane?.title || '';
    this.icon = plane?.icon || '';
    this.codeTemplate = plane?.codeTemplate || '';
    this.nodes = plane?.nodes || {};
    this.links = plane?.links || [];
    this.forms = plane?.forms;
    this.form = plane?.form || [];
    this.view = plane?.view || {};
    this.filterCategory = plane?.filterCategory || {};
    this.filters = plane?.filters || {};
    this.owner = plane?.owner || '';
    this.actions = plane?.actions || {};
    this.activityActions = plane?.activityActions || {};
    this.actionParams = plane?.actionParams || {};
    this.filesUploadSettings = plane?.filesUploadSettings || {};
    this.caseFileCondition = plane?.caseFileCondition || '';

  }
}
export interface RegsitryLink {
  id: string;
  code: string;
  output: string;
  isIfNode: boolean;
  node: NodeDefinition;
}

export interface NodeElement {
  type: string;
  label: string;
  node: NodeDefinition;
  isIfNode: boolean;
  output: string;
}
/*
export interface  MakinaRoleSchema  {
    details: boolean,
    commentRead: boolean,
    commentWrite: boolean,
    submit: boolean,
    assign: boolean,
    selfAssign: boolean
}
export interface  MakinaJsonSchema  {
  code: string,
  label: string,
  title: string,
  codeTemplate: string,
  actions: {
    owner: MakinaRoleSchema,
    group: MakinaRoleSchema,
    caseOwner: MakinaRoleSchema,
    caseCreatedBy: MakinaRoleSchema,
    roles: {
      ROLE_MAIN: MakinaRoleSchema,
      ROLE_ADMIN: MakinaRoleSchema,
      ROLE_USER: MakinaRoleSchema
      
    },
    groups: {
      GROUP_ADMIN: MakinaRoleSchema,
    }
  },
  activityActions: {
    owner: MakinaRoleSchema,
    group: MakinaRoleSchema,
    caseOwner: MakinaRoleSchema,
    caseCreatedBy: MakinaRoleSchema,
    roles: {
      ROLLE_MAIN: MakinaRoleSchema,
      R_USER_API_ADMIN: MakinaRoleSchema,
      R_WRITE_ALL: MakinaRoleSchema,
      ROLE_SECOND : MakinaRoleSchema,
      ROLE_THIRD : MakinaRoleSchema,
    },
    groups: {
      GROUP_ADMIN: MakinaRoleSchema,

    }
  },
  nodes: ActivityNodeDefinition,
  links: [
    {
      source: {
        node: string,
        output: string
      },
      target: {
        node: string,
        input: string
      }
    }
  ],
  forms: {
    // formName: { ... }
  },
  form: [
    {
      code: string,
      readonly: true,
      data: string
    }
  ]
};
*/
/*export interface JsonFileDto {
  nodes: NodeDto[];
  // Add other top-level properties as needed
}*/

export type SaveJsonResult = {
  json?: string;
  error?: Error;
};

